import {
  Button,
  Checkbox as TextVisibilityCheckbox,
  CheckboxChangeEvent,
  Flex,
} from "antd";
import { Dropdown, Toggle } from "@libertify/ui";
import { PlusIcon } from "@phosphor-icons/react";
import { CustomText, ITEM_TYPES, TemplateType } from "../../../types";
import { useTranslate } from "@refinedev/core";
import { PanelSection } from "../PanelSection";
import { nanoid } from "nanoid";
import {
  DEFAULT_PARAGRAPH_RELATIVE_FONT_SIZE,
  DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE,
  DEFAULT_SUBTITLES_LINE_HEIGHT,
  DEFAULT_TEXT_POSITION,
  DEFAULT_TEXT_SIZE,
  DEFAULT_TITLE_RELATIVE_FONT_SIZE,
} from "../../../constants";
import { BrandKitAsset } from "pages/media/types";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useChangeLayer } from "../../../hooks/useChangeLayer";
import { SettingSlider } from "../../controls/SettingSlider";
import { SettingsLabel } from "../../controls/SettingsLabel";
import { useMemo } from "react";
import { ApplySettingsFeature } from "../../features/ApplySettingsFeature";
import { ItemAlignmentFeature } from "../../features/ItemAlignmentFeature";
import {
  getCurrentTextAction,
  getCurrentTextItem,
} from "../../../helpers/textHelpers";
import {
  getSlotsForSlideTypeFromTheme,
  resolveFontAssetIdToBrandKitPlaceholder,
  resolveColorToBrandKitPlaceholder,
} from "../../../helpers/itemsHelper";
import { mapThemeSlotToTemplateItems } from "../../../helpers/mapThemeSlotToTemplateItems";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { TextStyleFeature } from "./features/TextStyleFeature";
import { usePublicMediaAssets } from "#src/hooks";

export enum TextType {
  TEXT = "text",
  TITLE = "title",
  SUBTITLE = "subtitles",
  PARAGRAPH = "paragraph",
}

export const TextTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();

  const {
    templatesData: { isFlashProject },
    projectTheme,
    storyboardWithSlidesData: { slides },
    brandKitData,
  } = useTemplateData();

  const { defaultPublicFont } = usePublicMediaAssets();

  const {
    textStyle,
    setTextStyle,
    addCustomText,
    titleStyle,
    setTitleStyle,
    currentChapter,
    currentItem,
    customTexts,
    parameteredTexts,
    updateCustomText,
    updateParameteredText,
    addParameteredTexts,
    figure,
    updateFigure,
    currentSlide,
    slideLayoutType,
  } = useTemplateEditorStore((store) => ({
    textStyle: store.templateState.textStyle,
    setTextStyle: store.setTextStyle,
    addCustomText: store.addCustomText,
    titleStyle: store.templateState.titleStyle,
    setTitleStyle: store.setTitleStyle,
    currentChapter: store.currentChapter,
    currentItem: store.templateState.currentItem,
    customTexts: store.templateState.customTexts,
    parameteredTexts: store.templateState.parameteredTexts,
    updateCustomText: store.updateCustomText,
    updateParameteredText: store.updateParameteredText,
    addParameteredTexts: store.addParameteredTexts,
    figure: store.templateState.figure,
    updateFigure: store.updateFigure,
    currentSlide: store.currentSlide,
    slideLayoutType: store.templateState.slideLayoutType,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const slots = useMemo(() => {
    if (!isFlashProject || !projectTheme || !slideLayoutType) return;
    return getSlotsForSlideTypeFromTheme(projectTheme, slideLayoutType);
  }, [isFlashProject, projectTheme, slideLayoutType]);

  const currentSlideData = useMemo(
    () => slides.find((s) => s.uuid === currentSlide),
    [slides, currentSlide]
  );

  const isTransition = currentChapter.type === TemplateType.TRANSITION;
  const isChapter = currentChapter.type === TemplateType.CHAPTER;
  const isSubtitles = currentItem?.type === ITEM_TYPES.Subtitles;

  const { titleFont, textFont, brandKitPalette, subtitlesFont } = brandKitData;
  const brandKitFonts = useMemo(
    () => [titleFont, subtitlesFont, textFont].filter((font) => !!font),
    [titleFont, subtitlesFont, textFont]
  );

  const item = getCurrentTextItem({
    currentItem,
    titleStyle,
    textStyle,
    customTexts,
    parameteredTexts,
    figure,
  });

  const currentSetAction = getCurrentTextAction({
    currentItem,
    setTitleStyle,
    setTextStyle,
    updateCustomText,
    updateParameteredText,
    updateFigure,
    customTexts,
    parameteredTexts,
    figure,
  });

  const { newHighestLayerIndex } = useChangeLayer(item ?? undefined);

  const onVisibilityCheckboxCheck = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    if (isTransition) {
      setTitleStyle({
        showTitle: checked,
        fontAssetId: resolveFontAssetIdToBrandKitPlaceholder(
          titleStyle.fontAssetId ??
            titleFont?.asset_id ??
            defaultPublicFont?.id,
          brandKitFonts
        ),
        color: resolveColorToBrandKitPlaceholder(
          brandKitPalette?.title_color ?? undefined,
          brandKitPalette
        ),
      });
    }
    if (isChapter) {
      setTextStyle({
        showSubtitles: checked,
        fontAssetId: resolveFontAssetIdToBrandKitPlaceholder(
          textStyle.fontAssetId ?? textFont?.asset_id ?? defaultPublicFont?.id,
          brandKitFonts
        ),
        color: resolveColorToBrandKitPlaceholder(
          brandKitPalette?.subtitle_color ?? undefined,
          brandKitPalette
        ),
      });
    }
    addTemplateUpdate();
  };

  const handleAddText = (type: TextType) => {
    let fontAsset: BrandKitAsset | undefined;
    let fontColor: string;
    if (type === TextType.TITLE) {
      fontAsset = titleFont;
      fontColor = resolveColorToBrandKitPlaceholder(
        brandKitPalette?.title_color ?? undefined,
        brandKitPalette
      );
    } else if (type === TextType.SUBTITLE) {
      fontAsset = subtitlesFont;
      fontColor = resolveColorToBrandKitPlaceholder(
        brandKitPalette?.subtitle_color ?? undefined,
        brandKitPalette
      );
    } else {
      fontAsset = textFont;
      fontColor = resolveColorToBrandKitPlaceholder(
        brandKitPalette?.subtitle_color ?? undefined,
        brandKitPalette
      );
    }

    const fontId = fontAsset?.asset_id ?? defaultPublicFont?.id;
    const fontPath = fontAsset?.path ?? defaultPublicFont?.path;

    const customText: CustomText = {
      id: `custom-text-${nanoid()}`,
      content: t(`components.templates.settings.textTab.textTypes.${type}`),
      fontSize: getNewTextFontSize(type),
      fontAssetId: resolveFontAssetIdToBrandKitPlaceholder(
        fontId,
        brandKitFonts
      ),
      fontPath: fontPath,
      color: fontColor,
      position: DEFAULT_TEXT_POSITION,
      size: DEFAULT_TEXT_SIZE,
      textAlign: "center",
      verticalAlign: "center",
      lineHeight: DEFAULT_SUBTITLES_LINE_HEIGHT,
      layerIndex: newHighestLayerIndex,
    };

    addCustomText(customText);
    addTemplateUpdate();
  };

  const lineHeightInPercentage = useMemo(() => {
    return Math.round(((item?.lineHeight ?? 0) + 1) * 100);
  }, [item?.lineHeight]);

  const handleLineHeightChange = (value: number) => {
    const lineHeight = value / 100 - 1;
    if (currentSetAction) {
      currentSetAction({ lineHeight });
      addTemplateUpdate();
    }
  };

  const addTextDropdownItems = [
    TextType.TITLE,
    TextType.SUBTITLE,
    TextType.PARAGRAPH,
  ].map((type) => ({
    key: type,
    label: t(`components.templates.settings.textTab.addText`, {
      text: t(
        `components.templates.settings.textTab.textTypes.${type}`
      ).toLowerCase(),
    }),
    onClick: () => handleAddText(type),
  }));

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.textTab.title")}
        subtitle={t("components.templates.settings.textTab.description")}
        primary
      >
        <Flex justify="center">
          <Dropdown items={addTextDropdownItems} trigger="click">
            <Button type="dashed" icon={<PlusIcon size={16} />}>
              {t("components.templates.settings.textTab.addText", {
                text: t(
                  "components.templates.settings.textTab.textTypes.text"
                ).toLowerCase(),
              })}
            </Button>
          </Dropdown>
        </Flex>
      </PanelSection>
      {slots ? (
        <PanelSection
          title={t("components.templates.settings.textTab.displayOption")}
        >
          <Flex vertical gap={8}>
            {slots.map((slot) => {
              const {
                parameteredText: parameteredTextSlot,
                figure: figureSlot,
              } = mapThemeSlotToTemplateItems(
                slot,
                projectTheme,
                currentSlideData,
                slideLayoutType
              );
              if (parameteredTextSlot) {
                const templateStateItem = parameteredTexts?.find(
                  (text) => text.path === parameteredTextSlot.path
                );
                return (
                  <Flex
                    key={`parameteredText-${slot.path}`}
                    align="center"
                    justify="space-between"
                  >
                    <SettingsLabel
                      type="secondary"
                      style={{ textTransform: "capitalize" }}
                    >
                      {parameteredTextSlot.path.replaceAll("_", " ")}
                    </SettingsLabel>
                    <Toggle
                      checked={templateStateItem?.visible}
                      onChange={(checked) => {
                        if (templateStateItem) {
                          updateParameteredText({
                            ...templateStateItem,
                            visible: checked,
                          });
                        } else if (checked) {
                          addParameteredTexts([
                            {
                              ...parameteredTextSlot,
                              visible: true,
                            },
                          ]);
                        }
                        addTemplateUpdate();
                      }}
                    />
                  </Flex>
                );
              }
              if (figureSlot) {
                return (
                  <Flex key="figure" vertical gap={8}>
                    <Flex align="center" justify="space-between">
                      <SettingsLabel type="secondary">
                        {t(
                          "components.templates.settings.textTab.figureNumber"
                        )}
                      </SettingsLabel>
                      <Toggle
                        checked={figure?.number.visible}
                        onChange={(checked) => {
                          if (figure) {
                            updateFigure({ visible: checked }, "number");
                          } else if (checked) {
                            updateFigure(
                              { ...figureSlot.number, visible: checked },
                              "number"
                            );
                          }
                          addTemplateUpdate();
                        }}
                      />
                    </Flex>
                    {figureSlot.units && (
                      <Flex align="center" justify="space-between">
                        <SettingsLabel type="secondary">
                          {t(
                            "components.templates.settings.textTab.figureUnits"
                          )}
                        </SettingsLabel>
                        <Toggle
                          checked={figure?.units?.visible}
                          disabled={!figure?.number.visible}
                          onChange={(checked) => {
                            if (figure?.units) {
                              updateFigure({ visible: checked }, "units");
                            } else if (checked) {
                              updateFigure(
                                { ...figureSlot.units, visible: checked },
                                "units"
                              );
                            }
                            addTemplateUpdate();
                          }}
                        />
                      </Flex>
                    )}
                  </Flex>
                );
              }
              return null;
            })}
          </Flex>
        </PanelSection>
      ) : (
        <PanelSection
          title={t("components.templates.settings.textTab.displayOption")}
        >
          {(isChapter || isTransition) && (
            <TextVisibilityCheckbox
              checked={
                isTransition ? titleStyle.showTitle : textStyle.showSubtitles
              }
              onChange={onVisibilityCheckboxCheck}
            >
              {t(
                `components.templates.settings.show${isTransition ? "Title" : "Subtitles"}`
              )}
            </TextVisibilityCheckbox>
          )}
        </PanelSection>
      )}
      {item && (
        <PanelSection
          title={t("components.templates.settings.textTab.textStyle")}
          noBorder
        >
          <TextStyleFeature />
        </PanelSection>
      )}
      {/* don't show line height slider if subtitles are not animated */}
      {item && (!isSubtitles || textStyle.animated) && (
        <PanelSection
          title={t("components.templates.settings.textTab.lineHeight")}
          noBorder
        >
          <SettingSlider
            value={lineHeightInPercentage}
            onChange={handleLineHeightChange}
            step={DEFAULT_SUBTITLES_LINE_HEIGHT * 100}
            valueSuffix="%"
            max={200}
            style={{ width: "70%", marginLeft: 0 }}
            containerStyle={{ padding: 0, justifyContent: "flex-start" }}
          />
        </PanelSection>
      )}
      {item && (
        <PanelSection
          title={t("components.templates.settings.textTab.alignment")}
          noBorder
        >
          <ItemAlignmentFeature
            pickedItem={item}
            onChange={(value) => {
              currentSetAction?.(value as Partial<CustomText>);
              addTemplateUpdate();
            }}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
          />
        </PanelSection>
      )}
      {!isFlashProject && (
        <PanelSection>
          <ApplySettingsFeature
            partialTemplateState={isChapter ? { textStyle } : { titleStyle }}
            itemType={isChapter ? ITEM_TYPES.Subtitles : ITEM_TYPES.Title}
          />
        </PanelSection>
      )}
    </>
  );
};

const getNewTextFontSize = (type: TextType) => {
  if (type === TextType.SUBTITLE) {
    return DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE;
  }
  if (type === TextType.PARAGRAPH) {
    return DEFAULT_PARAGRAPH_RELATIVE_FONT_SIZE;
  }
  return DEFAULT_TITLE_RELATIVE_FONT_SIZE;
};
